import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Paper,
} from '@mui/material';
import {
  Close as CloseIcon,
  PhoneAndroid as MobileIcon,
  Tablet as TabletIcon,
  Computer as DesktopIcon,
  // NavigateNext as NextIcon,
  // NavigateBefore as PrevIcon,
} from '@mui/icons-material';
import { ReactFormGenerator } from 'react-form-builder2';
import 'react-form-builder2/dist/app.css';
import { FormTheme } from '../../types';
import { generateThemeCSS, generateLayoutCSS, backgroundSx } from '../../utils/themeUtils';
import { formWidthSx } from '../../utils/formWidth';
import { fillTemplate } from '../../utils/templateText';
import { PreviewThemeRail } from './PreviewThemeRail';
import { FormStepper, getStepperPosition } from '../shared/FormStepper';
import { DeviceFrame, DeviceId, frameSize } from '../shared/DeviceFrame';
import { useFitScale } from '../../hooks/useFitScale';
import { ThemePreset } from './ThemePresets';
import { FormRuntimeContext } from '../form-renderer/FormRuntimeContext';
import { ADMIN_COLORS, FormSurfaceTheme } from '../../theme';

interface FormPreviewModalProps {
  open: boolean;
  onClose: () => void;
  formData: any[];
  formType?: 'single' | 'multistep';
  steps?: string[];
  theme?: FormTheme | null;
  /** When provided, the theme rail is shown and "Apply" commits the tried-on
   *  preset back to the builder's form theme. */
  onApplyTheme?: (theme: FormTheme) => void;
}

type DeviceType = 'mobile' | 'tablet' | 'desktop';

/** The toolbar speaks in form factors; the chassis has real hardware names. */
const DEVICE_ID: Record<DeviceType, DeviceId> = {
  desktop: 'macbook',
  tablet: 'ipad',
  mobile: 'iphone',
};

export const FormPreviewModal: React.FC<FormPreviewModalProps> = ({
  open,
  onClose,
  formData,
  formType = 'single',
  steps = [],
  theme,
  onApplyTheme,
}) => {
  const [deviceType, setDeviceType] = useState<DeviceType>('desktop');
  const [currentStep, setCurrentStep] = useState(0);
  const [railOpen, setRailOpen] = useState(true);
  const [tryOn, setTryOn] = useState<ThemePreset | null>(null);
  const isMultiStep = formType === 'multistep';

  const showRail = Boolean(onApplyTheme);
  /** The preset being tried on wins on the canvas; otherwise the saved theme. */
  const activeTheme: FormTheme | null = tryOn
    ? ({ ...(theme || {}), ...tryOn.theme, presetId: tryOn.id } as FormTheme)
    : theme || null;

  const handleClose = () => {
    setCurrentStep(0);
    setTryOn(null);
    onClose();
  };

  const handleApply = () => {
    if (!tryOn || !onApplyTheme || !activeTheme) return;
    onApplyTheme(activeTheme);
    handleClose();
  };
  const safeFormData = Array.isArray(formData) ? formData : [];

  const handleDeviceChange = (
    _event: React.MouseEvent<HTMLElement>,
    newDevice: DeviceType | null,
  ) => {
    if (newDevice !== null) {
      setDeviceType(newDevice);
    }
  };

  const deviceId = DEVICE_ID[deviceType];
  const deviceSize = frameSize(deviceId);
  const { ref: stageRef, scale, measured } = useFitScale({
    contentWidth: deviceSize.width,
    contentHeight: deviceSize.height,
    padding: { x: 48, y: 48 },
  });
  const previewStepperPosition = getStepperPosition(activeTheme);
  const showPreviewStepper =
    isMultiStep && steps.length > 0 && previewStepperPosition !== 'hidden';

  const previewRuntime = React.useMemo(
    () => ({
      customValues: {},
      advanceStep: () =>
        setCurrentStep((s) => Math.min(s + 1, Math.max(0, steps.length - 1))),
    }),
    [steps.length],
  );

  const previewThemeCSS =
    generateLayoutCSS('.form-preview-modal', deviceType === 'mobile') +
    generateThemeCSS(activeTheme, '.form-preview-modal', true);

  React.useEffect(() => {
    const win = window as any;
    win.__formActions = {
      next: () => {
        try {
          setCurrentStep((s) => {
            const next = Math.min(s + 1, Math.max(0, steps.length - 1));
            return next;
          });
        } catch (e) {}
      },
      prev: () => {
        try {
          setCurrentStep((s) => Math.max(0, s - 1));
        } catch (e) {}
      },
      submit: () => {
        try {
          if (isMultiStep && steps && steps.length > 0) {
            setCurrentStep((s) => Math.min(s + 1, Math.max(0, steps.length - 1)));
          }
        } catch (e) {}
      },
    };

    win.formRendererActions = win.__formActions;

    return () => {
      try {
        delete win.__formActions;
      } catch (e) {}
      try {
        delete win.formRendererActions;
      } catch (e) {}
    };
  }, [isMultiStep, steps]);

  React.useEffect(() => {
    const win = window as any;
    if (!win.data) win.data = {};
    return () => {};
  }, []);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullScreen
    >
      <DialogTitle
        sx={{
          position: 'relative',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 1,
          borderBottom: '1px solid',
          borderColor: 'divider',
          background: `linear-gradient(90deg, ${ADMIN_COLORS.accentSoft} 0%, ${ADMIN_COLORS.surface} 55%)`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
          <Typography variant="h6">{tryOn ? tryOn.name : 'Form Preview'}</Typography>
          {tryOn && (
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'warning.main' }}>
              (Preview)
            </Typography>
          )}
        </Box>
        <ToggleButtonGroup
          value={deviceType}
          exclusive
          onChange={handleDeviceChange}
          size="small"
          aria-label="device selection"
          sx={{
            position: 'absolute',
            left: '50%',
            transform: 'translateX(-50%)',
            backgroundColor: ADMIN_COLORS.segmentBg,
            borderRadius: '10px',
            p: 0.5,
            gap: 0.5,
            '& .MuiToggleButtonGroup-grouped': {
              border: 0,
              borderRadius: '10px !important',
              color: ADMIN_COLORS.textMuted,
              minWidth: 40,
              px: 1.5,
              '&.Mui-selected': {
                backgroundColor: ADMIN_COLORS.surface,
                color: ADMIN_COLORS.text,
                boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
                '&:hover': {
                  backgroundColor: ADMIN_COLORS.surface,
                },
              },
              '&:hover': {
                backgroundColor: ADMIN_COLORS.hover,
              },
            },
          }}
        >
          <ToggleButton value="desktop" aria-label="desktop">
            <DesktopIcon fontSize="small" />
          </ToggleButton>
          <ToggleButton value="tablet" aria-label="tablet">
            <TabletIcon fontSize="small" />
          </ToggleButton>
          <ToggleButton value="mobile" aria-label="mobile">
            <MobileIcon fontSize="small" />
          </ToggleButton>
        </ToggleButtonGroup>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {showRail && (
            <Button
              variant="contained"
              disableElevation
              disabled={!tryOn}
              onClick={handleApply}
              sx={{
                height: 36,
                px: '18px',
                borderRadius: '8px',
                fontSize: '13.5px',
                fontWeight: 600,
                textTransform: 'none',
                bgcolor: ADMIN_COLORS.accent,
                boxShadow: '0 1px 2px rgba(16, 24, 40, 0.1)',
                '&:hover': {
                  bgcolor: ADMIN_COLORS.accentHover,
                  boxShadow: `0 2px 6px ${ADMIN_COLORS.accentRing}`,
                },
                '&:active': { transform: 'translateY(1px)' },
                '&.Mui-disabled': { bgcolor: ADMIN_COLORS.accentDisabled, color: '#ffffff' },
              }}
            >
              Apply
            </Button>
          )}
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              color: (t) => t.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ p: 0, display: 'flex', overflow: 'hidden' }}>
        <Box
          ref={stageRef}
          sx={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: ADMIN_COLORS.canvas,
            overflow: 'hidden',
          }}
        >
        <DeviceFrame device={deviceId} scale={scale} hidden={!measured}>
        <FormSurfaceTheme>
        <Box
          className="form-preview-modal"
          sx={{
            minHeight: '100%',
            background: activeTheme?.background || '#f5f7fa',
            position: 'relative',
          }}
        >
          <style>{previewThemeCSS}</style>
          <Box
            sx={{
              display: 'flex',
              flexDirection: previewStepperPosition === 'left' ? 'row' : 'column',
              alignItems:
                previewStepperPosition === 'left' ? 'flex-start' : 'stretch',
              gap: previewStepperPosition === 'left' ? 3 : 0,
              borderRadius: `${activeTheme?.borderRadius ?? 0}px`,
              overflow: 'hidden',
              ...formWidthSx(activeTheme),
              ...backgroundSx(activeTheme),
            }}
          >
          {showPreviewStepper && previewStepperPosition !== 'bottom' && (
            <Paper
              elevation={0}
              sx={{
                py: `${(activeTheme?.formPaddingY ?? 32) / (deviceType === 'desktop' ? 1 : 2)}px`,
                px: `${(activeTheme?.formPaddingX ?? 32) / (deviceType === 'desktop' ? 1 : 2)}px`,
                flex: previewStepperPosition === 'left' ? '0 0 200px' : undefined,
                background: 'transparent',
                border: 'none',
                boxShadow: 'none',
              }}
            >
              <FormStepper
                steps={steps}
                activeStep={currentStep}
                theme={activeTheme}
              />
            </Paper>
          )}

          <Paper
            elevation={0}
            sx={{
              py: `${(activeTheme?.formPaddingY ?? 32) / (deviceType === 'desktop' ? 1 : 2)}px`,
              px: `${(activeTheme?.formPaddingX ?? 32) / (deviceType === 'desktop' ? 1 : 2)}px`,
              background: 'transparent',
              border: 'none',
              boxShadow: 'none',
            }}
          >
            {(() => {
              const getFieldsForCurrentStep = () => {
                if (!isMultiStep || !steps || steps.length === 0) {
                  return safeFormData;
                }
                return safeFormData.filter((field: any) => field.step === currentStep);
              };

              const currentStepFields = getFieldsForCurrentStep();

              return (
                <>
                  {currentStepFields.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 8 }}>
                      <Typography variant="h6" color="text.secondary">
                        No fields in this step
                      </Typography>
                    </Box>
                    ) : (
                    <FormRuntimeContext.Provider value={previewRuntime}>
                    <Box>
                      <ReactFormGenerator
                                data={((): any[] => {
                                  try {
                                    const dataObj = (window as any).data || {};
                                    const compile = (f: any) => {
                                      if (!f) return f;
                                      const nf = { ...f };
                                      const keysToReplace = [
                                        'label',
                                        'text',
                                        'content',
                                        'description',
                                        'placeholder',
                                        'question',
                                        'src',
                                        'href',
                                      ];
                                      const replaceTemplatePlaceholders = fillTemplate;

                                      keysToReplace.forEach((k) => {
                                        if (nf[k] && typeof nf[k] === 'string') {
                                          try {
                                            nf[k] = replaceTemplatePlaceholders(nf[k], dataObj);
                                          } catch (e) {}
                                        }
                                      });

                                      if (Array.isArray(nf.options)) {
                                        nf.options = nf.options.map((opt: any) => {
                                          if (!opt) return opt;
                                          const no = { ...opt };
                                          if (no.text && typeof no.text === 'string') {
                                            no.text = replaceTemplatePlaceholders(no.text, dataObj);
                                          }
                                          if (no.label && typeof no.label === 'string') {
                                            no.label = replaceTemplatePlaceholders(no.label, dataObj);
                                          }
                                          return no;
                                        });
                                      }

                                      if (nf.data && typeof nf.data === 'object') {
                                        if (nf.data.href && typeof nf.data.href === 'string') {
                                          nf.data.href = replaceTemplatePlaceholders(nf.data.href, dataObj);
                                        }
                                       if (nf.data.hrefTemplate && typeof nf.data.hrefTemplate === 'string') {
                                          nf.data.hrefTemplate = replaceTemplatePlaceholders(nf.data.hrefTemplate, dataObj);
                                        }
                                      }

                                      return nf;
                                    };

                                    return (currentStepFields || []).map(compile);
                                  } catch (e) {
                                    return currentStepFields;
                                  }
                                })()}
                        key={String(currentStep)}
                        form_action=""
                        form_method="POST"
                        answer_data={[]}
                        hide_actions={true}
                      />
                    </Box>
                    </FormRuntimeContext.Provider>
                  )}

                </>
              );
            })()}
          </Paper>

          {showPreviewStepper && previewStepperPosition === 'bottom' && (
            <Paper
              elevation={0}
              sx={{
                py: `${(activeTheme?.formPaddingY ?? 32) / (deviceType === 'desktop' ? 1 : 2)}px`,
                px: `${(activeTheme?.formPaddingX ?? 32) / (deviceType === 'desktop' ? 1 : 2)}px`,
                mt: 3,
                borderRadius: `${activeTheme?.borderRadius ?? 0}px`,
                ...backgroundSx(activeTheme),
                border: 'none',
                boxShadow: 'none',
              }}
            >
              <FormStepper
                steps={steps}
                activeStep={currentStep}
                theme={activeTheme}
              />
            </Paper>
          )}
          </Box>
        </Box>
        </FormSurfaceTheme>
        </DeviceFrame>
        </Box>

        {showRail && (
          <PreviewThemeRail
            open={railOpen}
            onToggle={() => setRailOpen((o) => !o)}
            selectedPresetId={tryOn?.id ?? theme?.presetId ?? null}
            onSelect={setTryOn}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};
